/**
 * D-EZ-110 — HOW A CHAPTER WRITES A SENTENCE.
 *
 * Every sentence in a chapter is a TEMPLATE plus SLOTS. The template carries
 * no digit and no name of its own; every name and number arrives through a
 * slot whose value was read off the league's record. `Told` keeps both, so a
 * test can strip the slots back out of the printed text and prove nothing was
 * left that the record does not hold (`chapters.grounding.test.ts`).
 *
 * Which of several templates is used is a hash of stable ids (`pick`) — never
 * an engine die — so a winter always reads the same way, and two winters read
 * differently.
 */

import { isRealDriver, stableHash } from '../story/hometown.ts';
import type { PeopleContext } from '../people/context.ts';
import { personalityOf } from '../people/context.ts';
import { fill, fillable, surname as lastWord } from '../people/quotes.ts';
import type { Voice } from '../people/personality.ts';
import { WINTER_BANK, type WinterFact } from './voices.ts';
import { toDollars } from '../finance/money.ts';

/**
 * Megabucks to the figure a newspaper prints — the same rule as the paper's
 * (`paper/fallbackPaper.ts` `dollars`), kept here so the chapters do not lean
 * on a file another layer owns.
 */
export function dollars(mb: number): string {
  const usd = toDollars(mb);
  if (usd >= 1_000_000) {
    const millions = usd / 1_000_000;
    return `$${millions >= 10 ? millions.toFixed(1) : millions.toFixed(2)} million`;
  }
  return `$${Math.round(usd).toLocaleString('en-US')}`;
}

export type SlotValue = string | number;

export interface Told {
  text: string;
  template: string;
  slots: Record<string, SlotValue>;
}

/** Every template a chapter has used this process — for the grounding test. */
export const TEMPLATES_USED = new Set<string>();

const SLOT = /\{(\w+)\}/g;

/** Fill a template. A slot the record cannot fill throws — a bug, never a guess. */
export function tell(template: string, slots: Record<string, SlotValue> = {}): Told {
  TEMPLATES_USED.add(template);
  const text = template.replace(SLOT, (_, k: string) => {
    if (!(k in slots)) throw new Error(`chapter template slot {${k}} not filled: ${template}`);
    return String(slots[k]);
  });
  const tidy = text
    // A race named "The Great American Getaway 400" after a template's "the".
    .replace(/\b[Tt]he (The )/g, '$1')
    // A hometown ending "N.C." at the end of a sentence.
    .replace(/\.\./g, '.');
  return { text: tidy.charAt(0).toUpperCase() + tidy.slice(1), template, slots };
}

/** Pick one of several templates by a stable key. */
export function pick<T>(key: string, options: readonly T[]): T {
  return options[stableHash(key) % options.length];
}

/** The templates whose slots these values can fill. */
export function usable(templates: readonly string[], slots: Record<string, SlotValue>): string[] {
  return templates.filter((t) => [...t.matchAll(SLOT)].every((m) => m[1] in slots && slots[m[1]] !== '' && slots[m[1]] !== null));
}

/** Pick and fill in one go, among the templates the slots can fill. */
export function tellOne(key: string, templates: readonly string[], slots: Record<string, SlotValue>): Told {
  const ok = usable(templates, slots);
  if (ok.length === 0) throw new Error(`no chapter template fits ${Object.keys(slots).join(',')}: ${templates[0]}`);
  return tell(pick(key, ok), slots);
}

/**
 * Season by season through the same templates: the league's own offset, plus
 * the season number — so two winters in a row never open the same way.
 */
export function cycle(key: string, n: number, templates: readonly string[], slots: Record<string, SlotValue>): Told {
  const ok = usable(templates, slots);
  if (ok.length === 0) throw new Error(`no chapter template fits ${Object.keys(slots).join(',')}: ${templates[0]}`);
  return tell(ok[(stableHash(key) + n) % ok.length], slots);
}

/**
 * A ROTOR: the same kind of sentence told several times in one chapter steps
 * through its templates instead of hashing each one independently, so two
 * retirements side by side never read alike (until the templates run out).
 */
export function rotor(key: string): (templates: readonly string[], slots: Record<string, SlotValue>) => Told {
  const base = stableHash(key);
  let i = 0;
  return (templates, slots) => {
    const ok = usable(templates, slots);
    if (ok.length === 0) throw new Error(`no chapter template fits ${Object.keys(slots).join(',')}: ${templates[0]}`);
    const t = ok[(base + i) % ok.length];
    i += 1;
    return tell(t, slots);
  };
}

/**
 * Headline names: a surname, unless another man the league knows shares it —
 * a generated field has three Turnages, and "Turnage retires" must say which.
 */
export function headlineNamer(names: Iterable<[string, string]>): (driverId: string, name: string) => string {
  const bySurname = new Map<string, Set<string>>();
  for (const [id, n] of names) {
    const s = surname(n);
    if (!bySurname.has(s)) bySurname.set(s, new Set());
    bySurname.get(s)!.add(id);
  }
  return (id, n) => {
    const s = surname(n);
    const ids = bySurname.get(s);
    return ids && [...ids].some((x) => x !== id) ? n : s;
  };
}

export function ordinal(n: number): string {
  const v = n % 100;
  const s = v >= 11 && v <= 13 ? 'th' : n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th';
  return `${n}${s}`;
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return n === 1 ? one : many;
}

const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve'];
/** A count the way a newspaper prints one: words to twelve, figures after. */
export function count(n: number): string {
  return n >= 0 && n < WORDS.length ? WORDS[n] : String(n);
}

const PARTICLES = new Set(['van', 'von', 'de', 'da', 'del', 'der', 'di', 'du', 'la', 'le']);

/** A surname the way the paper prints it: "van Gisbergen", "Earnhardt" (no "Jr."). */
export function surname(name: string): string {
  const last = lastWord(name);
  const parts = name.trim().split(/\s+/);
  const i = parts.lastIndexOf(last);
  // i > 1: a particle is never his first name (Del Pennington is a Pennington).
  if (i > 1 && PARTICLES.has(parts[i - 1].toLowerCase())) return `${parts[i - 1]} ${last}`;
  return last;
}

/** A driver's words between seasons. */
export interface Said {
  driverId: string;
  name: string;
  voice: Voice;
  fact: WinterFact;
  text: string;
  slots: Record<string, string>;
}

/**
 * His words for a winter fact, in his voice — or null for a real 2025 driver
 * (no words in a real man's mouth), or when no line fits the slots.
 */
export function winterQuote(
  fact: WinterFact,
  driverId: string,
  name: string,
  ctx: PeopleContext,
  slots: Record<string, string>,
  key: string,
  /** Lines already used in this chapter: a second speaker gets a different one while any is left. */
  used?: Set<string>,
): Said | null {
  if (isRealDriver(driverId)) return null;
  const voice = personalityOf(driverId, ctx).voice;
  let lines = WINTER_BANK[voice][fact].filter((l) => fillable(l, slots));
  if (lines.length === 0) lines = WINTER_BANK.plain[fact].filter((l) => fillable(l, slots));
  if (lines.length === 0) return null;
  const start = stableHash(`winter-quote|${ctx.leagueKey}|${driverId}|${fact}|${key}`) % lines.length;
  let line = lines[start];
  if (used) {
    for (let i = 0; i < lines.length; i++) {
      const l = lines[(start + i) % lines.length];
      if (!used.has(l)) {
        line = l;
        break;
      }
    }
    used.add(line);
  }
  return { driverId, name, voice, fact, text: fill(line, slots), slots };
}
